import {
  Fragment,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon } from '../atoms/Icon';
import type { IconName } from '../icons/registry';
import { usePresence } from '../hooks/usePresence';
import styles from './Menu.module.css';
import { MENU_EDGE, placeMenu } from './menuPosition';

export interface MenuItem {
  key: string;
  label: string;
  /** Optional symbol; menus are text first (docs/ux-richtlinien.md §2). */
  icon?: IconName;
  /** Destructive items are red and belong at the end of the menu. */
  tone?: 'default' | 'danger';
  disabled?: boolean;
  /** Shows a check mark, e.g. for the current sort option. */
  checked?: boolean;
  /** Draws a separator above this item. */
  separated?: boolean;
  onSelect: () => void;
}

/**
 * A few options side by side, like a segmented control (e.g. light / dark /
 * system). Its options are menu radio items, so arrow keys reach them; the
 * menu stays open on select, so the change is visible at once.
 */
export interface MenuChoice {
  key: string;
  /** Accessible name of the group, e.g. "Darstellung". */
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  separated?: boolean;
}

export interface MenuProps {
  /** Renders the trigger; spread the props onto a `Button`. */
  trigger: (props: {
    id: string;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
    onClick: () => void;
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
    ref: (element: HTMLElement | null) => void;
  }) => ReactNode;
  items: (MenuItem | MenuChoice)[];
  /** Horizontal alignment of the popup relative to the trigger. */
  align?: 'start' | 'end';
  /** Opens above the trigger, e.g. at the bottom of a sidebar. */
  placement?: 'bottom' | 'top';
  /** Optional content above the items, e.g. the signed-in person. */
  header?: ReactNode;
  /** Trigger and popup take the full width of the container. */
  block?: boolean;
  /** Controlled state, e.g. for a row menu that a right-click opens, too. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Opens at this point (viewport coordinates) instead of below the trigger,
   * like a context menu; it moves with the trigger while the page scrolls.
   */
  anchorPoint?: { x: number; y: number } | null;
}

function menuItems(menu: HTMLElement | null): HTMLElement[] {
  return Array.from(
    menu?.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not(:disabled), [role="menuitemradio"]:not(:disabled)',
    ) ?? [],
  );
}

/**
 * Places the open menu in the top layer (popover API), so no stacking context
 * of a row or card can paint over it, and anchors it to `anchor` with fixed
 * coordinates. Opens on the other side if there is no room. Without the
 * popover API (tests) the menu stays absolutely positioned below the trigger.
 */
function useTopLayer(
  open: boolean,
  present: boolean,
  menu: HTMLElement | null,
  anchor: HTMLElement | null,
  align: 'start' | 'end',
  placement: 'bottom' | 'top',
  block: boolean,
  point: { x: number; y: number } | null,
) {
  useLayoutEffect(() => {
    if (!open || !menu || !anchor || typeof menu.showPopover !== 'function') return;
    if (!menu.matches(':popover-open')) menu.showPopover();
    // The side is chosen once on open, so the menu does not jump while scrolling.
    let side: 'bottom' | 'top' | undefined;
    // A point is kept relative to the trigger, so it scrolls with the row.
    const start = anchor.getBoundingClientRect();
    const offset = point ? { x: point.x - start.left, y: point.y - start.top } : null;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const r = offset ? new DOMRect(a.left + offset.x, a.top + offset.y, 0, 0) : a;
      const { clientWidth: width, clientHeight: height } = document.documentElement;
      // Measure at the widest and tallest the viewport allows, so long labels wrap.
      Object.assign(menu.style, {
        maxWidth: `${width - 2 * MENU_EDGE}px`,
        maxHeight: '',
        width: block ? `${r.width}px` : '',
      });
      const placed = placeMenu({
        anchor: r,
        menu: { width: menu.offsetWidth, height: menu.scrollHeight },
        viewport: { width, height },
        align,
        placement,
      });
      side ??= placed.side;
      menu.dataset.side = side;
      const room = side === placed.side ? placed.maxHeight : height - MENU_EDGE * 2;
      Object.assign(menu.style, {
        top: side === 'bottom' ? `${r.bottom}px` : 'auto',
        bottom: side === 'top' ? `${height - r.top}px` : 'auto',
        left: block ? `${r.left}px` : `${placed.left}px`,
        right: 'auto',
        maxHeight: `${room}px`,
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, menu, anchor, align, placement, block, point]);

  // Leaves the top layer once the exit transition has finished (or on unmount).
  useEffect(() => {
    if (present || !menu?.matches(':popover-open')) return;
    menu.hidePopover();
  }, [present, menu]);
  useEffect(
    () => () => {
      if (menu?.matches(':popover-open')) menu.hidePopover();
    },
    [menu],
  );
}

/**
 * Menu button (WAI-ARIA menu pattern): arrow keys, Home/End, Escape and Tab
 * close the menu and focus returns to the trigger.
 */
export function Menu({
  trigger,
  items,
  align = 'start',
  placement = 'bottom',
  header,
  block = false,
  open: controlledOpen,
  onOpenChange,
  anchorPoint = null,
}: MenuProps) {
  const id = useId();
  const triggerId = `${id}-trigger`;
  const menuId = `${id}-menu`;
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlledOpen ?? ownOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setOwnOpen(next);
    onOpenChange?.(next);
  };
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [menu, setMenu] = useState<HTMLDivElement | null>(null);
  const { present, state } = usePresence(open, menuRef);
  useTopLayer(open, present, menu, root, align, placement, block, anchorPoint);
  const focusOnOpen = useRef<'first' | 'last'>('first');

  const close = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const elements = menuItems(menuRef.current);
    (focusOnOpen.current === 'last' ? elements[elements.length - 1] : elements[0])?.focus();
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        setOpenRef.current(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusOnOpen.current = e.key === 'ArrowUp' ? 'last' : 'first';
      setOpen(true);
    }
  };

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const elements = menuItems(menuRef.current);
    const index = elements.indexOf(document.activeElement as HTMLElement);
    const move = (next: number) => {
      e.preventDefault();
      elements[(next + elements.length) % elements.length]?.focus();
    };
    // Left and right move within a choice, like in a segmented control.
    const inChoice = (document.activeElement as HTMLElement | null)?.closest('[role="group"]');
    switch (e.key) {
      case 'ArrowDown':
        return move(index + 1);
      case 'ArrowUp':
        return move(index - 1);
      case 'ArrowRight':
        if (inChoice && inChoice === elements[index + 1]?.closest('[role="group"]')) {
          move(index + 1);
        }
        return;
      case 'ArrowLeft':
        if (inChoice && inChoice === elements[index - 1]?.closest('[role="group"]')) {
          move(index - 1);
        }
        return;
      case 'Home':
        return move(0);
      case 'End':
        return move(elements.length - 1);
      case 'Escape':
        e.preventDefault();
        return close();
      case 'Tab':
        return close(false);
    }
  };

  return (
    <div ref={setRoot} className={[styles.root, block && styles.block].filter(Boolean).join(' ')}>
      {trigger({
        id: triggerId,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': menuId,
        onClick: () => {
          focusOnOpen.current = 'first';
          setOpen(!open);
        },
        onKeyDown: onTriggerKeyDown,
        ref: (element) => {
          triggerRef.current = element;
        },
      })}
      <div
        ref={(element) => {
          menuRef.current = element;
          setMenu(element);
        }}
        id={menuId}
        role="menu"
        popover="manual"
        aria-labelledby={triggerId}
        hidden={!present}
        data-state={state}
        tabIndex={-1}
        onKeyDown={onMenuKeyDown}
        className={[styles.menu, styles[align], styles[placement]].join(' ')}
      >
        {header && (
          <div className={styles.header} role="presentation">
            {header}
          </div>
        )}
        {items.map((item) => {
          if ('options' in item) {
            return (
              <Fragment key={item.key}>
                {item.separated && <div className={styles.separator} role="separator" />}
                <div role="group" aria-label={item.label} className={styles.choice}>
                  {item.options.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={o.value === item.value}
                      tabIndex={-1}
                      className={[styles.choiceOption, o.value === item.value && styles.chosen]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => item.onChange(o.value)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </Fragment>
            );
          }
          const selectable = items.some((i) => 'checked' in i && i.checked !== undefined);
          return (
            <Fragment key={item.key}>
              {item.separated && <div className={styles.separator} role="separator" />}
              <button
                type="button"
                role={item.checked !== undefined ? 'menuitemradio' : 'menuitem'}
                {...(item.checked !== undefined ? { 'aria-checked': item.checked } : {})}
                tabIndex={-1}
                disabled={item.disabled}
                className={[styles.item, item.tone === 'danger' && styles.danger]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  close();
                  item.onSelect();
                }}
              >
                {selectable && (
                  <span className={styles.check} aria-hidden="true">
                    {item.checked && <Icon name="check" size="sm" />}
                  </span>
                )}
                {item.icon && <Icon name={item.icon} size="sm" className={styles.icon} />}
                {item.label}
              </button>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export interface MoreMenuProps {
  /** Accessible name of the `…` button, e.g. "Weitere Aktionen". */
  label: string;
  items: MenuItem[];
  align?: 'start' | 'end';
}

/** The `…` button for secondary actions of a page or row (docs/ux-richtlinien.md §4). */
export function MoreMenu({ label, items, align = 'end' }: MoreMenuProps) {
  return (
    <Menu
      align={align}
      items={items}
      trigger={(p) => (
        <button {...p} type="button" aria-label={label} className={styles.more}>
          <Icon name="more-horizontal" size="md" />
        </button>
      )}
    />
  );
}
